<template>
  <v-container v-if="$store.state.isUserLoggedIn" fill-height class="pa-0" style="overflow: hidden;">
    <v-row no-gutters>
      <v-col>
        <v-card tile :height="mainPanelHeight + 100">
          <v-toolbar height="30" class="pa-0 opposite--text" dense flat color="primary">
            <span class="mr-2 font-weight-bold text-h6">/</span>
            <span v-if="myAcademyData">{{ myAcademyData.academyName }}</span>
            <span v-else>...</span>
            <v-spacer></v-spacer>
            <v-btn @click="navigateTo({ name: 'myacademytab', params: { tabId: 0 }, query: { alerts: 'yes' } })"
              v-if="myAcademyData && myAcademyData.alertsCount" x-small elevation="0" text color="error">
              <v-icon size="25">mdi-chat-alert-outline</v-icon>
            </v-btn>
          </v-toolbar>
          <v-card-text class="pa-1" style="height: calc(100% - 30px); overflow: hidden;">
            <v-row dense style="height: 100%;">
              <v-col cols="8" style="height: 100%;">
                <v-card flat style="height: 100%;">
                  <div class="schedule-container" style="height: 100%; display: flex; flex-direction: column;">

                    <div class="d-flex align-center">
                      <v-select v-model="scheduleTab" :items="selectableTabs" item-text="text" item-value="value"
                        hide-details outlined dense class="mr-2"></v-select>
                    </div>

                    <v-tabs-items v-model="scheduleTab" style="flex: 1; min-height: 0; height: 100%;">

                      <v-tab-item value="today" class="pa-0" style="height: 100%;">
                        <v-skeleton-loader v-if="loadingClasses" type="list-item-two-line"></v-skeleton-loader>
                        <div v-else style="height: 100%; overflow-y: auto;">
                          <div v-if="scheduleNotes" class="text-center pa-1 error--text mb-0 pb-0">{{
                            scheduleNotes.notes
                          }}
                          </div>
                          <div v-if="!upcomingEvents || !upcomingEvents.length" class="pa-4 text-center">
                            No Classes to Show
                          </div>
                          <v-list v-else>
                            <v-list-item v-for="(e, index) in upcomingEvents" :key="'upcoming_event_' + index" outlined
                              tile @click="e._id ? goToAcademyClass(e) : null" class="pr-0 mb-1"
                              :class="{ clickable: !!e._id }"
                              :style="'border-left: 4px solid ' + getEventColor(e) + '; padding-left: 16px;'">
                              <v-list-item-content style="padding-left: 4px;">
                                <v-list-item-title class="d-flex align-center">
                                  <v-icon v-if="e.approved && e.approved.indexOf($store.state.user._id) >= 0" size="15"
                                    color="success" class="mr-1">mdi-check-circle</v-icon>
                                  <v-icon v-if="e.notes && e.notes !== ''" size="15" color="accent"
                                    class="mr-1">mdi-note-text-outline</v-icon>
                                  {{ truncate(e.title, 28) }}
                                </v-list-item-title>
                                <v-list-item-subtitle>{{ $d(new Date(e.date), "long", $i18n.locale)
                                  }}</v-list-item-subtitle>
                                <v-list-item-subtitle v-if="e.professorUser">{{ e.professorUser.displayName
                                  }}</v-list-item-subtitle>
                              </v-list-item-content>
                              <v-list-item-avatar v-if="e.professorUser && e.professorUser.userImageSmall">
                                <ImageDisplay :src="e.professorUser.userImageSmall" />
                              </v-list-item-avatar>
                            </v-list-item>
                          </v-list>
                        </div>
                      </v-tab-item>

                      <v-tab-item v-if="hasAvailableClasses" value="available" style="height: 100%;">
                        <v-skeleton-loader v-if="loadingClasses" type="list-item-two-line"></v-skeleton-loader>
                        <div v-else style="height: 100%; overflow-y: auto;">
                          <v-list two-line>
                            <template v-for="(e, index2) in availableClasses.filter(c => !c.isOtherAcademy)">
                              <v-list-item :key="'academy_class_' + index2" @click="goToAcademyClass(e)"
                                :style="'border-left: 4px solid ' + getEventColor(e) + '; padding-left: 16px;'">
                                <v-list-item-content>
                                  <v-list-item-title>{{ e.title }}</v-list-item-title>
                                  <v-list-item-title v-if="e.disabled" class="error--text">{{ $t("message.cancelled")
                                    }}</v-list-item-title>
                                  <v-list-item-subtitle>
                                    <span v-if="e.maxCapacity > 0"
                                      :class="'font-weight-bold mr-1 ' + (e.hasCapacity ? 'success--text' : 'error--text')">
                                      {{ e.approved ? e.approved.length : 0 }}\{{ e.maxCapacity }}
                                    </span>
                                    <span v-if="e.professorUser">{{ e.professorUser.displayName }}</span>
                                  </v-list-item-subtitle>
                                  <v-list-item-subtitle v-if="e.date">{{ $d(new Date(e.date), "long", $i18n.locale)
                                    }}</v-list-item-subtitle>
                                </v-list-item-content>

                                <v-list-item-action v-if="e.status || e.canJoin || e.canWaitlist">
                                  <v-btn :loading="processingClassAction" @click.stop="joinAcademyClass(e)"
                                    color="primary" icon small v-if="!e.status && e.canJoin">
                                    <v-icon>mdi-login</v-icon>
                                  </v-btn>
                                  <v-btn :loading="processingClassAction" @click.stop="joinAcademyClass(e)"
                                    color="secondary" icon small v-else-if="!e.status && e.canWaitlist">
                                    <v-icon>mdi-login</v-icon>
                                  </v-btn>
                                  <v-btn icon :color="e.canWaitlist ? 'error' : 'secondary'" small
                                    v-else-if="e.status == 'pending'">
                                    <v-icon>mdi-clock-time-five-outline</v-icon>
                                  </v-btn>
                                  <v-btn color="success" icon small v-else-if="e.status == 'approved'">
                                    <v-icon>mdi-checkbox-marked-circle-outline</v-icon>
                                  </v-btn>
                                </v-list-item-action>
                              </v-list-item>
                            </template>
                          </v-list>
                        </div>
                      </v-tab-item>

                      <v-tab-item value="full" style="height: 100%;">
                        <v-skeleton-loader v-if="loadingClasses" type="list-item-two-line"></v-skeleton-loader>
                        <div v-else style="height: 100%; overflow-y: auto;">
                          <div v-if="scheduleNotes" class="text-center pa-1 error--text mb-0 pb-0">{{
                            scheduleNotes.notes }}</div>
                          <div class="pl-2">
                            <ScheduleDisplay :professors="professors" :schedule="schedule" />
                          </div>
                        </div>
                      </v-tab-item>

                    </v-tabs-items>
                  </div>
                </v-card>
              </v-col>
              <v-divider vertical></v-divider>
              <v-col cols="4" style="height: 100%; overflow-y: auto;">
                <v-card flat>
                  <v-btn class="mt-1" @click="viewSceduleFullscreen()" outlined small block color="primary"
                    elevation="0">
                    {{ $t("message.my_calendar") }}
                    <v-spacer></v-spacer>
                  </v-btn>
                  <v-btn class="mt-1" @click="navigateTo({ name: 'viewposts', params: { feed: 'academy' } })" outlined
                    small block color="primary" elevation="0">
                    {{ $t("message.bulletin") }}
                    <v-spacer></v-spacer>
                    <span class="text--secondary" text v-if="bulletinData.last24">
                      +{{ bulletinData.last24 }}
                    </span>
                  </v-btn>
                  <v-btn @click="navigateTo({ name: 'videos' })" outlined small block color="primary" class="mt-1">
                    {{ $t("message.videos") }}
                    <v-spacer></v-spacer>
                  </v-btn>
                  <v-btn @click="navigateTo({ name: 'lineagetree', query: { n: myAcademyData.academyLineageNode } })"
                    outlined small block color="primary" class="mt-1">
                    {{ $t("message.lineage") }}
                    <v-spacer></v-spacer>
                  </v-btn>
                  <v-btn @click="navigateTo({ name: 'myacademytab', params: { tabId: 1 } })" outlined small block
                    color="primary" class="mt-1">
                    {{ $t("message.payments") }}
                    <v-spacer></v-spacer>
                  </v-btn>
                  <v-btn v-if="myAcademyData && myAcademyData.hasProducts" outlined small block color="primary"
                    class="mt-1" @click="navigateTo({ name: 'academyproducts' })">
                    {{ $t("message.products") }}
                    <v-spacer></v-spacer>
                  </v-btn>
                  <v-btn outlined small block color="primary" class="mt-1"
                    @click="navigateTo({ name: 'myacademytab', params: { tabId: 0 } })">
                    {{ $t("message.view_more") }}
                    <v-spacer></v-spacer>
                  </v-btn>
                  <div v-if="myAcademyData" class="mt-2">
                    <div v-if="myAcademyData.progress && myAcademyData.progress.value >= 0"
                      class="d-flex flex-column align-center mt-1 px-1 clickable" @click="goToInstance()">
                      <v-progress-circular :width="6" :size="48" v-if="myAcademyData.progress.jjtemplate"
                        :color="myAcademyData.progress.color" :value="myAcademyData.progress.value">
                        <span style="font-size:10px;font-weight:bold;">{{ myAcademyData.progress.value }}%</span>
                      </v-progress-circular>
                      <v-progress-circular :width="6" :size="48" v-else :color="myAcademyData.progress.level"
                        :value="myAcademyData.progress.value">
                        <span style="font-size:10px;font-weight:bold;">{{ myAcademyData.progress.value }}%</span>
                      </v-progress-circular>
                      <span class="text-caption mt-1">{{ $t("message.progress") }}</span>
                    </div>
                    <div v-if="points && points.positions && points.positions.academyPosition"
                      class="align-center pb-0 px-1 clickable" @click="pointsDialog = true">
                      <div class="text-center">
                        <span class="text-caption" style="padding-bottom:0;">
                          <b>{{ points.positions.academyPosition }}</b>
                          {{ $t("message.in_your_academy").toLowerCase() }}
                        </span>
                      </div>
                    </div>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-divider></v-divider>
    <v-row no-gutters>
      <v-col>

        <v-card tile :height="mainPanelHeight - 90" class="d-flex flex-column">
          <v-toolbar height="30" class="pa-0 opposite--text flex-grow-0" dense flat color="primary">
            <span class="mr-2 font-weight-bold text-h6">/</span>
            <span @click="navigateTo({ name: 'viewmytrainingtab', params: { tabId: 0 } })">{{ $t("message.my_training")
            }}</span>
            <v-spacer></v-spacer>
            <v-menu offset-y>
              <template v-slot:activator="{ on }">
                <v-btn color="opposite" v-on="on" icon small slot="activator">
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item @click="getMyTrainingData()">
                  <v-list-item-title>
                    <v-icon class="mr-1">mdi-sync</v-icon>
                    {{ $t('message.refresh') }}
                  </v-list-item-title>
                </v-list-item>
                <v-list-item @click="navigateTo({ name: 'viewmytrainingtab', params: { tabId: 1 } })">
                  <v-list-item-title>
                    <v-icon class="mr-1">mdi-book-open-variant-outline</v-icon>
                    {{ $t('message.journal') }}
                  </v-list-item-title>
                </v-list-item>
                <v-list-item @click="pointsDialog = true;">
                  <v-list-item-title>
                    <span class="mr-1 font-weight-bold">{{ points.total }}</span>
                    {{ $t("message.points") }}
                  </v-list-item-title>
                </v-list-item>
                <v-list-item @click="navigateTo({ name: 'viewmytrainingtab', params: { tabId: 0 } })()">
                  <v-list-item-title>
                    <v-icon class="mr-1">mdi-dots-horizontal</v-icon>
                    {{ $t('message.view_more') }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-toolbar>



          <div class="flex-grow-1 d-flex flex-column" :style="`height: ${mainPanelHeight - 120}px`">

            <v-row no-gutters style="margin-top:5px; height: 75%; overflow: hidden;">
              <v-col class="pt-2">
                <v-row>
                  <v-col v-if="myTrainingData.loading" class="text-center">
                    <v-progress-circular :width="7" :size="100" color="grey lighten-2"
                      indeterminate></v-progress-circular>
                  </v-col>
                  <v-col v-else class="pa-0 ma-0">
                    <ApexChart style="margin-top:-15px" v-if="myTrainingData.radial" type="radialBar" height="140"
                      :options="myTrainingData.radial.options" :series="myTrainingData.radial.series">
                    </ApexChart>
                    <div class="text-center" style="margin-top:-27px;font-size:11px">
                      {{ $t("message.vs_last_month", [myTrainingData.lastMonthMinutes]) }}
                    </div>
                  </v-col>
                  <v-col v-if="myTrainingData.loading">
                    <v-skeleton-loader type="article"></v-skeleton-loader>
                  </v-col>
                  <v-col v-else class="pa-0 ma-0 pl-2">
                    <v-card elevation="0">
                      <div class="text-subtitle-1"><b>{{ $t("message.total_checkins") }}</b></div>
                    </v-card>
                    <div class="text-caption">
                      <div>
                        <v-icon size="20" class="mr-1">mdi-all-inclusive-box-outline</v-icon>{{ $t("message.lifetime")
                        }}:
                        <b>{{ myTrainingData.totalCheckins }}</b>
                      </div>
                      <div>
                        <v-icon size="20" class="mr-1">mdi-clock-time-three-outline</v-icon>{{ $t("message.today") }}:
                        {{ myTrainingData.todayTotalCheckins }}
                      </div>
                      <div>
                        <v-icon size="20" class="mr-1">mdi-clock-time-six-outline</v-icon>{{ $t("message.this_week") }}:
                        {{ myTrainingData.thisWeekTotalCheckins }}
                      </div>
                      <div>
                        <v-icon size="20" class="mr-1">mdi-clock-time-nine-outline</v-icon>{{ $t("message.this_month")
                        }}: {{ myTrainingData.thisMonthTotalCheckins }}
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <v-row no-gutters style="height: 25%; min-height: 25%; max-height: 25%;" class="overflow-hidden">
              <v-col v-if="myTrainingData.loading">
                <v-skeleton-loader type="list-item"></v-skeleton-loader>
              </v-col>
              <v-col v-else class="pa-0 fill-height d-flex align-center px-4">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on">
                      <div class="text-subtitle-1" v-if="myTrainingData.thisMonthMinutesOnDay == 0">
                        <v-icon class="mr-2">mdi-trending-neutral</v-icon>
                        <b>{{ $t("message.get_started") }}</b>
                        {{ $t("message.get_started_explained") }}
                      </div>
                      <div class="text-subtitle-1" v-else-if="myTrainingData.messageSentiment == 'neutral'">
                        <v-icon class="mr-2">mdi-trending-neutral</v-icon>
                        <b>{{ $t("message.stay_steady") }}</b>
                        {{ $t("message.stay_steady_explained") }}
                      </div>
                      <div class="text-subtitle-1" dense v-else-if="myTrainingData.messageSentiment == 'positive'">
                        <v-icon color="success" class="mr-2">mdi-trending-up</v-icon>
                        <b>{{ $t("message.keep_it_up") }}</b>
                        {{ $t("message.keep_it_up_explained") }}
                      </div>
                      <div class="text-subtitle-1" dense v-else>
                        <v-icon color="error" class="mr-2">mdi-trending-down</v-icon>
                        <b>{{ $t("message.step_it_up") }}</b>
                        {{ $t("message.step_it_up_explained") }}
                      </div>
                    </div>
                  </template>
                  <span>{{ $t("message.comparison_month_at_date", [myTrainingData.lastMonthMinutesOnDay,
                  myTrainingData.thisMonthMinutesOnDay]) }}</span>
                </v-tooltip>
              </v-col>
            </v-row>

          </div>
        </v-card>



      </v-col>
    </v-row>
    <v-divider></v-divider>
    <v-row no-gutters>
      <v-col cols="6">
        <v-card tile flat :min-height="lowerPanelsHeight" @click="go2MatchTracker">
          <v-toolbar height="30" class="pa-0 opposite--text text-truncate" dense flat color="primary">
            <span class="mr-2 font-weight-bold text-h6">/</span>
            {{ $t("message.match_tracker") }}
          </v-toolbar>
          <div v-if="matchTrackerData.loading">
            <v-skeleton-loader class="mx-auto" type="image"></v-skeleton-loader>
          </div>
          <div v-else>
            <v-list dense>
              <v-list-item dense>
                <v-list-item-content class="pt-0 pb-0 mb-0 mt-0">
                  <v-list-item-title>{{ $t("message.saved") }}</v-list-item-title>
                  <v-list-item-title>{{ $t("message.competitors") }}</v-list-item-title>
                </v-list-item-content>
                <v-list-item-action class="pl-1 ml-1 pa-0">
                  <v-btn x-small elevation="0" fab>
                    {{ matchTrackerData.savedCompetitorsCount }}
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
              <v-list-item dense>
                <v-list-item-content class="pt-0 pb-0 mb-0 mt-0">
                  <v-list-item-title>{{ $t("message.active") }}</v-list-item-title>
                  <v-list-item-title>{{ $t("message.competing") }}</v-list-item-title>
                </v-list-item-content>
                <v-list-item-action class="pl-1 ml-1 pa-0 mb-0 mt-0">
                  <v-btn x-small elevation="0" fab>
                    {{ matchTrackerData.savedCompetitorsCompetingCount }}
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
              <v-list-item dense>
                <v-list-item-content class="pt-0 pb-0 mb-0 mt-0">
                  <v-list-item-title>{{ $t("message.active") }}</v-list-item-title>
                  <v-list-item-title>{{ $t("message.events") }}</v-list-item-title>
                </v-list-item-content>
                <v-list-item-action>
                  <v-btn x-small elevation="0" fab>
                    {{ matchTrackerData.activeEventsCount }}
                  </v-btn>
                </v-list-item-action>
                <v-list-item-avatar size="30" class="pl-1 ml-1 pa-0" outlined>
                  <img :src="assets() + '/external-logos/ibjjf-logo-blue.png'" />
                </v-list-item-avatar>
              </v-list-item>
            </v-list>
            <v-card-actions class="pb-0 pt-0" style="margin-top:-2px">
              <v-btn elevation="0" @click="go2MatchTracker" block text small color="primary">
                <span class="mr-1">{{ $t("message.view_more") }}</span>
              </v-btn>
            </v-card-actions>
          </div>
        </v-card>
      </v-col>
      <v-col cols="6">
        <v-card tile flat :min-height="lowerPanelsHeight">
          <v-toolbar height="30" class="pa-0 opposite--text" dense flat color="primary">
            <span class="mr-2 font-weight-bold text-h6">/</span>
            {{ $t("message.news") }}
          </v-toolbar>
          <div v-if="newsData.loading">
            <v-skeleton-loader class="mx-auto" type="image"></v-skeleton-loader>
          </div>
          <div v-else>
            <v-list dense>
              <template v-for="(i, index) in newsData.last3">
                <v-list-item @click="openRssLink(i)" :key="'home_news_' + index" dense class="pt-0 pb-0 mb-0 mt-0">
                  <v-list-item-content>
                    <v-list-item-title class="text-truncate" style="overflow: hidden;">
                      {{ truncate(i.title, 20) }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-truncate" style="overflow: hidden;">
                      {{ truncate(i.description, 20) }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-avatar tile class="pa-0" size="30">
                    <v-img
                      :src="`https://wsrv.nl/?url=${encodeURIComponent(i.image)}&default=https://via.placeholder.com/30`" />
                  </v-list-item-avatar>
                </v-list-item>
              </template>
            </v-list>
            <v-card-actions class="pb-0 pt-0">
              <v-btn elevation="0" @click="go2News" block text small color="primary">
                <span class="mr-1">{{ $t("message.new_today") }}</span>
                <span class="text-h6">{{ newsData.newToday }}</span>
              </v-btn>
            </v-card-actions>
          </div>
        </v-card>
      </v-col>
    </v-row>
    <v-dialog v-model="joinClassDialog">
      <v-card outlined v-if="selectedClass">
        <v-card-text class="text-subtitle-1">
          {{ $t("message.join") }}: {{ selectedClass.title }}
        </v-card-text>
        <v-card-text>
          <v-select outlined dense v-model="selectedFamilyMember" :items="familyMembers" item-text="text"
            item-value="value" prepend-inner-icon="mdi-account-group-outline">
          </v-select>
        </v-card-text>
        <v-card-actions>
          <v-btn :loading="processingClassAction" color="primary" outlined elevation="0"
            @click="joinAcademyClass(selectedClass, selectedFamilyMember)">{{
              $t("message.join")
            }}</v-btn>
          <v-spacer></v-spacer>
          <v-btn elevation="0" @click="joinClassDialog = null; selectedClass = null;">{{ $t("message.close")
            }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="pointsDialog">
      <v-card outlined v-if="points.positions">
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn icon small @click="pointsDialog = false"><v-icon>mdi-close-circle</v-icon></v-btn>
        </v-card-actions>
        <v-card-title>{{ $t("message.points") }}</v-card-title>
        <v-list dense>
          <v-list-item dense>
            <v-list-item-content>
              {{ $t("message.total_points") }}
            </v-list-item-content>
            <v-list-item-action>
              {{ points.total }}
            </v-list-item-action>
          </v-list-item>
          <v-divider></v-divider>
          <v-list-item dense>
            <v-list-item-content>
              {{ $t("message.last_30_days") }}
            </v-list-item-content>
            <v-list-item-action>
              {{ points.last30Days }}
            </v-list-item-action>
          </v-list-item>
          <v-divider></v-divider>
          <v-list-item dense>
            <v-list-item-content>
              {{ $t("message.this_month") }}
            </v-list-item-content>
            <v-list-item-action>
              {{ points.thisMonth }}
            </v-list-item-action>
          </v-list-item>
          <v-divider></v-divider>
          <v-list-item dense>
            <v-list-item-content>
              {{ $t("message.academy_position") }}
            </v-list-item-content>
            <v-list-item-action>
              {{ points.positions.academyPosition }}
            </v-list-item-action>
          </v-list-item>
          <v-divider></v-divider>
          <v-list-item dense>
            <v-list-item-content>
              {{ $t("message.city_position") }}
            </v-list-item-content>
            <v-list-item-action>
              {{ points.positions.cityPosition }}
            </v-list-item-action>
          </v-list-item>
          <v-divider></v-divider>
          <v-list-item dense>
            <v-list-item-content>
              {{ $t("message.country_position") }}
            </v-list-item-content>
            <v-list-item-action>
              {{ points.positions.countryPosition }}
            </v-list-item-action>
          </v-list-item>
          <v-divider></v-divider>
          <v-list-item dense>
            <v-list-item-content>
              {{ $t("message.world_position") }}
            </v-list-item-content>
            <v-list-item-action>
              {{ points.positions.worldPosition }}
            </v-list-item-action>
          </v-list-item>
        </v-list>
        <v-card-actions>
          <v-btn elevation="0" @click="navigateTo({ name: 'points' })" block>{{ $t("message.view_more") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="scheduleDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card outlined>
        <v-toolbar flat class="opposite--text" color="primary">
          <v-toolbar-title>{{ $t("message.my_calendar") }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn color="opposite" small icon @click="scheduleDialog = false">
            <v-icon>mdi-close-circle-outline</v-icon>
          </v-btn>
        </v-toolbar>
        <div>
          <div>
            <v-card-actions v-if="checkinCode" outlined class="mb-0 pb-0 pt-3">
              <div>
                <div class="text-subtitle-2 text-center">{{ $t("message.your_checkin_code") }}</div>
                <div class="text-center text-h3">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <div class="text-center text-h3" v-bind="attrs" v-on="on">{{ checkinCode }}</div>
                    </template>
                    <span>{{ $t('message.use_app_to_checkin') }}</span>
                  </v-tooltip>
                </div>
              </div>
              <v-spacer></v-spacer>
              <v-btn elevation="0" outlined color="primary" @click="loadSchedule()">{{ $t("message.refresh") }}</v-btn>
            </v-card-actions>

            <div v-if="loadingClasses">
              <v-skeleton-loader class="mx-auto" type="table"></v-skeleton-loader>
            </div>
            <v-card-text v-else class="pa-2">
              <CalendarDisplay :events="calendarEvents" :loading="loadingClasses" @join-class="joinAcademyClass"
                @view-event="viewEventFromCalendar" />
            </v-card-text>
          </div>
        </div>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import CalendarDisplay from "@/components/displays/CalendarDisplay.vue";
import ScheduleDisplay from "@/components/displays/ScheduleDisplay.vue";
import AcademyCalendarService from "@/services/AcademyCalendarService";
import AcademyService from "@/services/AcademyService";
import ConnectService from "@/services/ConnectService";
import NewsService from "@/services/NewsService";
import NotificationService from "@/services/NotificationService";
import ApexChart from 'vue-apexcharts';

export default {
  name: 'HomeMember',
  data() {
    return {
      myAcademyData: null,
      mainPanelHeight: 280,
      lowerPanelsHeight: 240,
      schedule: null,
      scheduleNotes: null,
      professors: [],
      availableClasses: null,
      joinClassDialog: false,
      selectedClass: null,
      isMember: false,
      hasAvailableClasses: false,
      loadingClasses: false,
      classesPanel: null,
      upcomingEvents: [],
      calendarEvents: [],
      checkinCode: null,
      pointsDialog: false,
      scheduleDialog: false,
      scheduleTab: 'today',
      selectedFamilyMember: null,
      processingClassAction: false,
      familyMembers: null,
      matchTrackerData: {
        loading: true,
        savedCompetitorsCount: 0,
        activeCompetingCount: 0,
        activeEventsCount: 0,
      },
      myTrainingData: {
        loading: true,
        last30Percent: 0,
        minutes: 0,
      },
      newsData: {
        loading: true,
      },
      bulletinData: {
        loading: true,
      },
      points: {
        loading: true,
      }
    };
  },
  components: {
    ScheduleDisplay,
    CalendarDisplay,
    ApexChart
  },
  props: ["refreshPage"],
  mounted() {
    const vm = this;
    vm.getMessagesCount(function (countM) {
      vm.getNotificationsCount(function (countS) {
        vm.$store.dispatch("setMessagesCount", countM);
        vm.$store.dispatch("setNotifications", countS);
        if (countM + countS == 0) {
          NotificationService.clearMobile();
        }
      });
    });
  },
  created() {
    this.initMe();
  },

  watch: {
    refreshPage: {
      immediate: false,
      handler: function (newValue) {
        if (newValue) {
          this.initMe();
        }
      }
    }
  },
  computed: {
    selectableTabs() {
      const options = [
        { text: this.$t("message.todays_schedule"), value: 'today' }
      ];

      if (this.hasAvailableClasses) {
        options.push({ text: this.$t("message.registration_required_classes"), value: 'available' });
      }

      options.push({ text: this.$t("message.full_schedule"), value: 'full' });

      return options;
    }
  },
  methods: {
    viewSceduleFullscreen() {
      this.scheduleDialog = true;
      this.loadSchedule();
    },
    async initMe() {
      const me = await ConnectService.getMe();
      if (me) {
        this.hiddenUser = me.data.hidden;
        if (me.data.isManagedAcademyAdmin && !this.$store.state.managedAcademyAdmin) {
          this.$store.dispatch("setManagedAcademyAdmin", true);
        } else if (!me.data.isManagedAcademyAdmin) {
          this.$store.dispatch("setManagedAcademyAdmin", false);
        }
        if (me.data.isManagedAcademyMember && (!this.$store.state.userManagedAcademy || this.$store.state.userManagedAcademy != me.data.mainManagedAcademy)) {
          this.$store.dispatch("setUserManagedAcademy", me.data.mainManagedAcademy);
        } else if (!me.data.isManagedAcademyMember) {
          this.$store.dispatch("setUserManagedAcademy", null);
        }
        if (!me.data.mainManagedAcademy) {
          this.navigateTo({ name: 'home-regular' })
        }
        let needsApp = me.data.needsApp;
        if (needsApp) {
          this.$store.dispatch("setNeedsApp", true);
        } else {
          this.$store.dispatch("setNeedsApp", false);
        }
        this.loadData();
      }
    },
    openRssLink(item) {
      if (item) {
        const data = {
          entryId: item._id
        };
        if (item.feed)
          NewsService.recordClick(item.feed._id, data);
        this.goToLink(item.url)
      }
    },
    go2MatchTracker() {
      if (this.hiddenUser)
        this.showAlert(
          "info",
          this.$t("message.social_disabled")
        );
      else
        this.navigateTo({ name: 'ibjjftournaments' })
    },
    go2News() {
      if (this.hiddenUser)
        this.showAlert(
          "info",
          this.$t("message.social_disabled")
        );
      else
        this.navigateTo({ name: 'news' })
    },
    goToInstance() {
      if (this.myAcademyData && this.myAcademyData.progress && this.myAcademyData.progress.showDetails)
        this.navigateTo({ name: "viewprogressdetails", params: { instanceId: this.myAcademyData.progress.instanceId } });
    },
    joinAcademyClass(data, memberId) {
      if (!memberId && this.familyMembers && this.familyMembers.length) {
        this.selectedClass = data;
        this.joinClassDialog = true;
        return;
      }
      this.processingClassAction = true;
      let academyClassId = data._id;
      const split = academyClassId.split("_");
      if (split && split.length == 2) {
        academyClassId = split[0] + "_" + (data.managedAcademy || this.$store.state.userManagedAcademy);
      }
      if (memberId)
        data.member = memberId;
      AcademyCalendarService.joinAcademyClassFromHome(academyClassId, data)
        .then((data) => {
          if (data) {
            this.loadSchedule();
            this.processingClassAction = false;
            this.joinClassDialog = false;
            this.showAlert("success", this.$t("message.success"));
          }
        })
        .catch((err) => {
          this.processingClassAction = false;
          if (err.response && err.response.status === 405)
            this.showAlert("error", this.$t("message.need_membership_for_class"));
          else
            this.showAlert("error", this.$t("message.oops_not_working"));
        });
    },
    viewEventFromCalendar(e) {
      if (!e._id || e._id.indexOf('_') > 0) return;
      this.scheduleDialog = false;
      if (e.social) {
        this.navigateTo({ name: 'viewevent', params: { eventId: e._id } });
      } else {
        this.navigateTo({ name: 'viewacademyclass', params: { academyClassId: e._id } });
      }
    },
    goToAcademyClass(e) {
      if (!e._id || e._id.indexOf('_') > 0)
        return;
      if (e.social) {
        this.navigateTo({
          name: "viewevent",
          params: { eventId: e._id },
        });
      } else if (this.isMember || e.allowNonMembers) {
        this.navigateTo({
          name: "viewacademyclass",
          params: { academyClassId: e._id },
        });
      }
    },
    loadSchedule() {
      this.getUpcomingEvents();
    },
    getUpcomingEvents() {
      this.loadingClasses = true;
      const today = new Date();
      today.setHours(0);
      const dayOfWeek = this.getDayString(today);
      AcademyCalendarService.upcomingEvents(today, dayOfWeek, true)
        .then((data) => {
          if (data.events && data.events.length > 0) {
            const tmp = data.events;
            if (tmp && tmp.length > 0) {
              let upcomingEvents = [];
              this.classesPanel = 0;
              const date = new Date();
              for (const i of tmp) {
                if (i.disabled) continue;
                if (i.date) {
                  const date = new Date(i.date);
                  upcomingEvents.push({
                    _id: i._id,
                    date: date,
                    title: i.title,
                    professorUser: i.professorUser,
                    start: date.getHours(),
                    approved: i.approved,
                    notes: i.notes,
                  });
                } else {
                  const d = this.hourInNumberToDate(i.start, date);
                  upcomingEvents.push({
                    _id: i._id,
                    date: d,
                    title: i.title,
                    start: i.start,
                    professorUser: i.professorUser,
                    approved: i.approved,
                    notes: i.notes,
                  });
                }
              }
              upcomingEvents.sort((e1, e2) => e1.date - e2.date)
              this.upcomingEvents = upcomingEvents;
            }
          }
          if (data.calendar && data.calendar.events && data.calendar.events.length > 0) {
            this.processCalendarEvents(data.calendar.events);
          } else if (data.events && data.events.length > 0) {
            const eventsWithDates = data.events.map(e => {
              if (!e.date) {
                const eventDate = new Date();
                eventDate.setHours(e.start || 0, 0, 0, 0);
                e.date = eventDate;
              }
              return e;
            });
            this.processCalendarEvents(eventsWithDates);
          } else {
            console.log('No calendar or events data received from API', data);
          }
          //Load next
          this.getAcademyClasses()
        });
    },
    getAcademyClasses() {
      AcademyService.academyClasses(this.$store.state.userManagedAcademy)
        .then((data) => {
          try {
            if (data.schedule)
              this.schedule = data.schedule;
            if (data.checkinCode)
              this.checkinCode = data.checkinCode;
            if (data.scheduleNotes)
              this.scheduleNotes = data.scheduleNotes;
            if (data.professors)
              this.professors = data.professors;
            this.availableClasses = [];
            const allCalendarEvents = [];
            if (data.familyMembers) {
              this.selectedFamilyMember = this.$store.state.user._id;
              let familyMembers = [{ text: this.$t("message.you"), value: this.$store.state.user._id }];
              this.familyMembers = familyMembers.concat(data.familyMembers);
            }
            if (data.isMember)
              this.isMember = data.isMember;
            if (data.events && data.events.length > 0) {
              this.hasAvailableClasses = true;
              for (let c of data.events) {
                const userId = this.$store.state.user._id;
                const maxCapacity = c.maxCapacity;
                const totalApproved = c.approved ? c.approved.length : 0;
                if (!c.disabled) {
                  if (totalApproved < maxCapacity)
                    c.hasCapacity = true;
                  if (c.approved && c.approved.indexOf(userId) >= 0) {
                    c["status"] = "approved";
                  } else if (c.pending && c.pending.indexOf(userId) >= 0) {
                    c["status"] = "pending";
                    if (c.maxCapacity) {
                      if (totalApproved >= maxCapacity) c["canWaitlist"] = true;
                    }
                  } else {
                    if (c.maxCapacity) {
                      if (totalApproved < maxCapacity) {
                        if (this.isMember || c.allowNonMembers)
                          c["canJoin"] = true;
                      } else if (c.allowWaitlist) {
                        if (this.isMember || c.allowNonMembers)
                          c["canWaitlist"] = true;
                      }
                    }
                  }
                }

                // Enrich isFromSchedule events from schedule data (assistantProfessors is not populated on generated occurrences)
                if (c.isFromSchedule && c.scheduleClassId && this.schedule) {
                  for (const day of Object.keys(this.schedule)) {
                    const entry = this.schedule[day].find(s => s._id === c.scheduleClassId);
                    if (entry) {
                      if (!c.assistantProfessors || !c.assistantProfessors.length) c.assistantProfessors = entry.assistantProfessors;
                      if (!c.professorUser && entry.professorUser) c.professorUser = { _id: entry.professorUser };
                      break;
                    }
                  }
                }

                // Professor/assistant role detection
                if (c.professorUser && c.professorUser._id === userId) {
                  c.isProfessor = true;
                } else if (c.assistantProfessors && c.assistantProfessors.some(p => (p._id || p) === userId)) {
                  c.isAssistantProfessor = true;
                }

                // Social event participation detection
                if (c.social && (c.isParticipant || (c.participants && c.participants.some(p => (p._id || p) === userId)))) {
                  c.isEventParticipant = true;
                }

                // Other academy detection — trust the API flag, or fall back to ID comparison
                if (!c.isOtherAcademy) {
                  const userAcademyId = this.$store.state.userManagedAcademy;
                  if (userAcademyId && c.managedAcademy && c.managedAcademy.toString() !== userAcademyId.toString()) {
                    c.isOtherAcademy = true;
                  }
                }

                const classTime = new Date(c.date);
                c.date = classTime;
                if (!isNaN(c.startTimeHours)) {
                  classTime.setHours(c.startTimeHours);
                  classTime.setMinutes(c.startTimeMinutes);
                  c.date = classTime;
                } else if (c.start) {
                  classTime.setHours(c.start);
                }
                if (c.classDay)
                  c.date.setDate(c.classDay);
                if (!c.disabled && Number.isFinite(c.maxCapacity) && c.maxCapacity >= 0) {
                  this.availableClasses.push(c);
                }
                allCalendarEvents.push(c);
              }
            }
            if (allCalendarEvents.length > 0) {
              this.mergeCalendarEvents(allCalendarEvents);
            }
          } catch (err) {
            console.log(err);
          }
          this.loadingClasses = false;
        });
    },
    mergeCalendarEvents(newEvents) {
      const existingIds = new Set(this.calendarEvents.map(e => e._id));
      const uniqueNewEvents = newEvents.filter(e => !existingIds.has(e._id));
      if (uniqueNewEvents.length > 0) {
        this.calendarEvents = [...this.calendarEvents, ...uniqueNewEvents];
      }
    },
    getEventColor(event) {
      if (event.isProfessor) return '#1565C0';
      if (event.isAssistantProfessor) return '#0288D1';
      if (event.isEventParticipant) return '#388E3C';
      if (event.social) return '#4CAF50';
      if (event.status === 'approved' || event.status === 'pending') return '#8d70b3';
      if (event.allowNonMembers) return '#fc9826';
      return '#9e9e9e';
    },
    getClassColor(event) {
      if (!this.schedule) return null;
      for (const day of Object.keys(this.schedule)) {
        for (const c of this.schedule[day]) {
          if (c.name === event.title && (event.start === undefined || c.start === event.start)) {
            return c.color || null;
          }
        }
      }
      return null;
    },
    processCalendarEvents(calendarData) {
      const userId = this.$store.state.user._id;
      const userAcademyId = this.$store.state.userManagedAcademy;
      this.calendarEvents = calendarData.map(c => {
        const maxCapacity = c.maxCapacity;
        const totalApproved = c.approved ? c.approved.length : 0;

        if (totalApproved < maxCapacity) {
          c.hasCapacity = true;
        }

        if (c.approved && c.approved.indexOf(userId) >= 0) {
          c.status = "approved";
        } else if (c.pending && c.pending.indexOf(userId) >= 0) {
          c.status = "pending";
          if (c.maxCapacity && totalApproved >= maxCapacity) {
            c.canWaitlist = true;
          }
        } else {
          if (c.maxCapacity) {
            if (totalApproved < maxCapacity) {
              if (this.isMember || c.allowNonMembers) {
                c.canJoin = true;
              }
            } else if (c.allowWaitlist) {
              if (this.isMember || c.allowNonMembers) {
                c.canWaitlist = true;
              }
            }
          }
        }

        // Professor/assistant role detection
        if (c.professorUser && c.professorUser._id === userId) {
          c.isProfessor = true;
        } else if (c.assistantProfessors && c.assistantProfessors.some(p => (p._id || p) === userId)) {
          c.isAssistantProfessor = true;
        }

        // Social event participation detection
        if (c.social && (c.isParticipant || (c.participants && c.participants.some(p => (p._id || p) === userId)))) {
          c.isEventParticipant = true;
        }

        // Other academy detection
        if (userAcademyId && c.managedAcademy && c.managedAcademy.toString() !== userAcademyId.toString()) {
          c.isOtherAcademy = true;
        }

        return c;
      });
    },
    getRadialProgressColor(value, colors) {
      if (value > 100) {
        return colors[2];
      }
      const hexToRgb = (hex) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return { r, g, b };
      };

      const componentToHex = (c) => {
        const hex = Math.round(c).toString(16);
        return hex.length === 1 ? "0" + hex : hex;
      };

      // 1. If value is 50 or less, return the first color immediately
      if (value <= 50) {
        return colors[0];
      }

      // 2. Clamp value to 100 max
      const val = Math.min(value, 100);

      // 3. Normalize the 51-100 range to a 0-100 scale for the transition
      // This makes 50 act like 0 and 100 act like 100 for the math below
      const adjustedVal = (val - 50) * 2;

      const c0 = hexToRgb(colors[0]); // This is your "Static" color until 50
      const c50 = hexToRgb(colors[1]);
      const c100 = hexToRgb(colors[2]);

      let start, end, pct;

      // 4. Determine which half of the REMAINING range we are in (50-75 or 75-100)
      if (adjustedVal < 50) {
        start = c0;
        end = c50;
        pct = adjustedVal / 50;
      } else {
        start = c50;
        end = c100;
        pct = (adjustedVal - 50) / 50;
      }

      const r = start.r + (end.r - start.r) * pct;
      const g = start.g + (end.g - start.g) * pct;
      const b = start.b + (end.b - start.b) * pct;

      return `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`;
    },
    getMyTrainingData() {
      this.myTrainingData = {
        loading: true,
        last30Percent: 0,
        minutes: 0,
      }
      ConnectService.getHomeMyTraining()
        .then((data) => {
          if (data.success) {
            this.myTrainingData = data.data;
            if (data.data.messageSentiment) {
              const thisMonth = this.myTrainingData.thisMonthMinutes;
              const lastMonth = this.myTrainingData.lastMonthMinutes;
              let percentage = lastMonth > 0 ? Math.round((thisMonth / lastMonth) * 100) : (lastMonth == 0 ? 100 : 0);
              const vm = this;
              //lets give it a nicer radial color
              let radialColor = this.getRadialProgressColor(percentage, [this.themeColor('error'), this.themeColor('yellow'), this.themeColor('success')]);
              if (percentage > 100)
                percentage = percentage - 100;
              this.myTrainingData.radial = {
                options: {
                  chart: {
                    background: 'transparent',
                    sparkline: {
                      enabled: false // ensure this is false if you want standard padding
                    }
                  },
                  theme: {
                    mode: this.isDarkTheme() ? 'dark' : 'light',
                  },
                  colors: [radialColor], // Green if beating last month
                  plotOptions: {
                    radialBar: {
                      hollow: { size: '60%' },
                      dataLabels: {
                        show: true,
                        name: {
                          show: true,
                          fontSize: '10px',
                          offsetY: -10,
                        },
                        value: {
                          show: true,
                          fontSize: '13px',
                          fontWeight: 700,
                          offsetY: -10,
                          // Show this month's actual minutes
                          formatter: function () {
                            return [thisMonth, vm.$t("message.minutes")];
                          }
                        }
                      }
                    }
                  },
                  tooltip: {
                    enabled: true,
                    shared: false,
                    intersect: true,
                    // Custom HTML for the tooltip
                    custom: function () {
                      return '<div class="pa-2">' +
                        '<span>Last Month: ' + lastMonth + ' min</span><br>' +
                        '<span>This Month: ' + thisMonth + ' min</span>' +
                        '</div>'
                    }
                  },
                  markers: {
                    size: 0
                  },
                  labels: [this.$t("message.this_month")],
                },
                series: [percentage > 100 ? 100 : percentage]
              };

              this.myTrainingData.loading = false;
            }
          }
        });
    },
    getCurrentWeekOfMonthGrouped() {
      const today = new Date();
      const year = today.getFullYear();
      const month = today.getMonth(); // 0-indexed
      const startOfMonth = new Date(year, month, 1);

      const getWeekNumber = (date) => {
        const day = date.getDate();
        const adjustedStart = (startOfMonth.getDay() + 6) % 7; // Adjust so Monday is 0
        return Math.floor((day + adjustedStart - 1) / 7) + 1;
      };

      let currentWeek = getWeekNumber(today);
      // Calculate total weeks in month
      const endOfMonth = new Date(year, month + 1, 0);
      const totalDays = endOfMonth.getDate();
      const lastDate = new Date(year, month, totalDays);
      const totalWeeks = getWeekNumber(lastDate);

      if (totalWeeks === 5) {
        if (currentWeek <= 2)
          currentWeek = 1;
      }

      //console.log('Current week end: ', currentWeek)
      return currentWeek;//we need 1-4 not 0-3
    },
    loadData() {
      this.loadSchedule();
      ConnectService.getHomePostsAcademy()
        .then((data) => {
          if (data.success) {
            this.bulletinData = data.data;
          }
        });
      ConnectService.getHomeMatchTracker()
        .then((data) => {
          if (data.success) {
            this.matchTrackerData = data.data;
          }
        });
      ConnectService.getHomePoints()
        .then((data) => {
          this.points = data.points;
          this.points.positions = data.positions;
        });
      this.getMyTrainingData();
      ConnectService.getHomeNews()
        .then((data) => {
          if (data.success) {
            if (data.data.news) {
              this.newsData = data.data;
              this.newsData.last3 = data.data.news.slice(0, 3);
            }
          }
        });
      ConnectService.getHomeMyAcademy()
        .then((data) => {
          if (data.success && data.data.academy) {
            let myAcademyData = { loading: false };
            myAcademyData.academyName = data.data.academy.name;
            myAcademyData.academyImage = data.data.academy.image;
            myAcademyData.alertsCount = data.data.alerts;
            myAcademyData.topUsers = data.data.topUsers;
            myAcademyData.hasProducts = data.data.hasProducts;
            myAcademyData.academyLineageNode = data.data.academyLineageNode;
            if (data.data.progress) {
              myAcademyData.progressPercent = data.data.progress.value;
              myAcademyData.progress = data.data.progress;
            }
            this.myAcademyData = myAcademyData;
          }
        });
    }
  }
}
</script>
<style scoped></style>
